import { select, Store } from '@ngrx/store';
import { DictionariesCollectionService } from '@core/data/dictionaries/dictionaries-collection.service';
import {
  Component,
  ElementRef,
  Inject,
  OnInit,
  Optional,
  ViewChild,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { ContactFormDialogComponent } from '../contact-form-dialog/contact-form-dialog.component';
import { SupportApiService } from '@core/services/api/support-api.service';
import { map, mergeMap } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';
import {
  isUserRoleDataPrivacyOfficerSelector,
  isUserRoleProviderSelector,
  userRoleNewSelector,
} from '@core/store/selectors/user-role.selectors';
import { SUPPORT_TOPICS } from '../../../constants/support-topics';
import { ActionDialogComponent } from '../../dialogs/action-dialog/action-dialog.component';
import { DialogData, SpinnerComponent, XtDialogComponent } from '@xt/ui-kit';
import { USER_ROLE } from '@core/store/reducers/session-info.reducer';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { MatExpansionModule } from '@angular/material/expansion';
import { CypressDirectiveModule } from '@shared/directives/cypress/cypress-directive.module';
import { ButtonModule } from '@shared/components/atoms/button/button.module';

@Component({
  selector: 'xt-portal-support-faq-dialog',
  templateUrl: './support-faq-dialog.component.html',
  styleUrls: ['./support-faq-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    XtDialogComponent,
    ButtonModule,
    MatExpansionModule,
    SpinnerComponent,
    CypressDirectiveModule,
  ],
})
export class SupportFaqDialogComponent implements OnInit {
  @ViewChild('videoContainer', { read: ElementRef })
  public videoContainer: ElementRef<any>;
  title: string;
  showContent: boolean = false;
  faq: any[] = [];
  videos: any[] = [];
  loading = false;

  userRole$: Observable<any>;
  isUserRoleProvider: boolean;
  isUserRoleDPO: boolean;

  topic: any;

  dialogConfig: DialogData;

  dialogTypes = {
    allFaqs: {
      title: 'app.dialog.support.faq',
      getFaqs: this.getAllFaqsAndVideos.bind(this),
      buttons: [
        {
          click: this.goToInfo.bind(this),
          text: 'app.labels.back',
          class: 'sub-button',
          cypress: 'dialog-info.close',
          style: 'mat-stroked-button',
        },
      ],
    },
    info: {
      title: 'app.dialog.info.title',
      getFaqs: this.getFaqsAndVideos.bind(this),
      buttons: [
        {
          click: this.goToAll.bind(this),
          text: 'app.dialog.info.allFaqs',
          class: 'sub-button',
          cypress: 'dialog-info.support.open-all',
          style: 'mat-stroked-button',
        },
        {
          click: this.openInfoForm.bind(this),
          text: 'app.dialog.info.make-contact',
          cypress: 'dialog-info.support.open-info',
        },
      ],
    },
  };

  constructor(
    public dialogRef: MatDialogRef<SupportFaqDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    public dialog: MatDialog,
    private dictionariesCollection: DictionariesCollectionService,
    private supportApiService: SupportApiService,
    private store: Store,
  ) {}

  ngOnInit() {
    this.title = this.dialogTypes[this.data.type]?.title;
    if (this.dialogTypes[this.data.type]?.getFaqs) {
      this.dialogTypes[this.data.type].getFaqs();
    }
    // fix expansion bug on opening dialog
    setTimeout(() => (this.showContent = true), 0);
    this.store
      .pipe(select(isUserRoleProviderSelector))
      .subscribe((isUserRoleProvider) => {
        this.isUserRoleProvider = isUserRoleProvider;
      });

    this.store
      .pipe(select(isUserRoleDataPrivacyOfficerSelector))
      .subscribe((isUserRoleDPO) => {
        this.isUserRoleDPO = isUserRoleDPO;
      });

    this.dialogConfig = {
      title: this.title,
    };
  }

  /**
   * Get faq list by topic id related on page topic
   */
  getFaqsAndVideos() {
    this.loading = true;
    this.userRole$ = this.store.pipe(select(userRoleNewSelector));

    this.dictionariesCollection
      .getItemById$('taxonomytermsupporttopics')
      .pipe(
        map(({ list }) => list?.find((item) => item.id === this.data?.topic)),
        mergeMap((topic) => {
          const params = {};

          this.userRole$.subscribe((role) => {
            if (this.data.topic) {
              this.topic = this.data.topic;
              if (role.includes(USER_ROLE.HCP)) {
                this.data.topic = this.data.topic + '_hcp';
              }
              params['topic'] = this.data.topic;
            }
          });

          return combineLatest([
            this.supportApiService.getFAQ('faq', params),
            this.supportApiService.getFAQ('video', params),
          ]);
        }),
      )
      .subscribe(([{ faqItems }, { videos }]) => {
        this.faq = faqItems;
        this.videos = videos;
        this.loading = false;
      });
  }

  getAllFaqsAndVideos() {
    this.supportApiService.getFAQ('faq', {}).subscribe((res) => {
      this.faq = res.faqItems;
    });

    this.supportApiService
      .getFAQ('video', {})
      .subscribe((res) => (this.videos = res.videos));
  }

  /**
   * closes the mat dialog
   */
  closeDialog(): void {
    this.dialogRef.close();
  }

  goToAll() {
    this.dialogRef.close();
    this.dialog.open(SupportFaqDialogComponent, {
      width: '880px',
      data: {
        type: 'allFaqs',
        previousTopic: this.topic,
      },
    });
  }

  goToInfo() {
    this.dialogRef.close();
    if (this.isUserRoleProvider || this.isUserRoleDPO) {
      if (this.data.previousTopic) {
        this.dialog.open(SupportFaqDialogComponent, {
          width: '880px',
          data: {
            topic: this.data?.previousTopic || SUPPORT_TOPICS.SUPPORT.code,
            type: 'info',
          },
        });
      } else {
        this.dialog.open(ActionDialogComponent, {
          width: '700px',
          panelClass: 'support-modal',
          data: {
            title: 'app.dialog.info.title',
            content: 'app.dialog.info.textPractitioner',
            confirmBtnText: 'app.labels.back',
            cancelBtnText: 'app.dialog.info.allFaqs',
            isSupportDialog: true,
          },
        });
      }
    } else {
      this.dialog.open(SupportFaqDialogComponent, {
        width: '880px',
        data: {
          topic: this.data?.previousTopic || SUPPORT_TOPICS.SUPPORT.code,
          type: 'info',
        },
      });
    }
  }

  openInfoForm(): void {
    this.dialogRef.close();
    this.dialog.open(ContactFormDialogComponent, {
      width: '810px',
      maxHeight: '90dvh',
    });
  }

  scrollRight(): void {
    if (this.videoContainer.nativeElement.clientWidth < 280) {
      this.videoContainer.nativeElement.scrollTo({
        left:
          this.videoContainer.nativeElement.scrollLeft +
          this.videoContainer.nativeElement.clientWidth,
        behavior: 'smooth',
      });
    } else {
      this.videoContainer.nativeElement.scrollTo({
        left: this.videoContainer.nativeElement.scrollLeft + 150,
        behavior: 'smooth',
      });
    }
  }

  scrollLeft(): void {
    if (this.videoContainer.nativeElement.clientWidth < 280) {
      this.videoContainer.nativeElement.scrollTo({
        left:
          this.videoContainer.nativeElement.scrollLeft -
          this.videoContainer.nativeElement.clientWidth,
        behavior: 'smooth',
      });
    } else {
      this.videoContainer.nativeElement.scrollTo({
        left: this.videoContainer.nativeElement.scrollLeft - 150,
        behavior: 'smooth',
      });
    }
  }
}
